/* Copyright (c) 2026 Xperi. All Rights Reserved. */

// Imported from the concrete module rather than the `../logging` barrel: this
// runs on the earliest boot path (a failure can arrive before Sage is
// initialised), same reasoning as clearAppStorage.
import { Log } from "../logging/Log";

const TAG: string = "silentReloadGuard";

/**
 * One-attempt guard for the silent recoveries the app performs behind the
 * user's back — reloading after a failed code-split chunk, restarting at the
 * root after a URL that matches no route.
 *
 * What must never happen is a loop: a TV that cannot reach the chunk host at
 * all, or a root that fails the same way, would recover forever and show
 * nothing but a black screen. Each attempt is therefore recorded before the
 * navigation, and a second attempt with the same signature within
 * {@link SILENT_RELOAD_COOLDOWN_MS} is refused so the caller can put a real
 * error in front of the user instead.
 *
 * `sessionStorage` is the only store that fits: the decision is needed
 * synchronously in a React render, and it must survive exactly one page load
 * (the app's own IndexedDB adapter is async, and `localStorage` would outlive
 * the launch).
 */

/** sessionStorage key holding the last silent-recovery attempt. */
export const SILENT_RELOAD_STORAGE_KEY: string = "unity.silentReloadAttempt";

/**
 * How long a recorded attempt blocks another silent recovery for the same
 * signature. Long enough that a hard failure cannot loop (each iteration would
 * need a full app boot plus this cooldown), short enough that a user who keeps
 * the app open for a while still gets a silent recovery from a later,
 * unrelated blip.
 */
export const SILENT_RELOAD_COOLDOWN_MS: number = 60000;

interface ISilentReloadRecord {
  /** Signature of the failure that triggered the recovery. */
  signature: string;
  /** `Date.now()` at the time of the attempt. */
  at: number;
}

/**
 * Outcome of reading the stored attempt. `isReadable` separates "no attempt
 * recorded yet" from "this store cannot be read": the second must never allow
 * a recovery, because an attempt we cannot read back is one we would repeat on
 * every boot.
 */
interface ISilentReloadReadResult {
  isReadable: boolean;
  record: ISilentReloadRecord | null;
}

/**
 * `window.sessionStorage`, or null when it is unreachable — an embedded TV
 * browser with storage disabled throws on property access, not just on use.
 */
function getSessionStorage(): Storage | null {
  try {
    const storage: Storage | undefined = window.sessionStorage;
    if (storage === null || storage === undefined) {
      return null;
    }
    return storage;
  } catch (error: unknown) {
    Log.w(TAG, "sessionStorage unavailable", error);
    return null;
  }
}

function readRecord(storage: Storage): ISilentReloadReadResult {
  let raw: string | null = null;
  try {
    raw = storage.getItem(SILENT_RELOAD_STORAGE_KEY);
  } catch (error: unknown) {
    Log.w(TAG, "Failed to read recovery record", error);
    return { isReadable: false, record: null };
  }

  if (raw === null || raw.length === 0) {
    return { isReadable: true, record: null };
  }

  try {
    const parsed = JSON.parse(raw) as {
      signature?: unknown;
      at?: unknown;
    };
    if (typeof parsed.signature !== "string" || typeof parsed.at !== "number") {
      return { isReadable: true, record: null };
    }
    return {
      isReadable: true,
      record: { signature: parsed.signature, at: parsed.at }
    };
  } catch (error: unknown) {
    // A corrupt value is not an unusable store: the next write replaces it.
    Log.w(TAG, "Discarding corrupt recovery record", error);
    return { isReadable: true, record: null };
  }
}

/**
 * Whether a silent recovery is still allowed for `signature`. Read-only: safe
 * to call during a React render to decide what to paint.
 *
 * With no usable `sessionStorage` the answer is always no. An attempt we cannot
 * remember is an attempt we would repeat on the next boot, which is the loop
 * this guard exists to prevent — better to show the error overlay once than to
 * black-screen the TV indefinitely.
 */
export function canAttemptSilentReload(signature: string): boolean {
  const storage: Storage | null = getSessionStorage();
  if (storage === null) {
    return false;
  }
  const result: ISilentReloadReadResult = readRecord(storage);
  if (!result.isReadable) {
    return false;
  }
  const record: ISilentReloadRecord | null = result.record;
  if (record === null) {
    return true;
  }
  if (record.signature !== signature) {
    return true;
  }
  return Date.now() - record.at >= SILENT_RELOAD_COOLDOWN_MS;
}

/**
 * Claims the one allowed attempt for `signature`. Returns false when it could
 * not be recorded — the caller must then NOT navigate, because an attempt that
 * leaves no trace is one that repeats on the next boot.
 */
export function recordSilentReloadAttempt(signature: string): boolean {
  const storage: Storage | null = getSessionStorage();
  if (storage === null) {
    return false;
  }
  try {
    const record: ISilentReloadRecord = {
      signature: signature,
      at: Date.now()
    };
    storage.setItem(SILENT_RELOAD_STORAGE_KEY, JSON.stringify(record));
    return true;
  } catch (error: unknown) {
    Log.w(TAG, "Failed to record recovery attempt", error);
    return false;
  }
}
